import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  ArrowLeft, HardHat, MapPin, FileText, ClipboardList, Receipt, Truck,
  IndianRupee, TrendingUp, TrendingDown, Calendar,
} from "lucide-react";

function fmtINR(n) {
  if (!n) return "₹0";
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${Number(n).toLocaleString("en-IN")}`;
}

const STATUS_COLORS = {
  planning: "bg-blue-500",
  active: "bg-orange-600",
  on_hold: "bg-yellow-500",
  completed: "bg-green-600",
};

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="overline">{label}</div>
          <div className="font-heading font-black text-2xl mt-1.5">{value}</div>
        </div>
        <div className={`h-8 w-8 flex items-center justify-center rounded-sm ${accent || "bg-zinc-100"}`}>
          <Icon className={`h-4 w-4 ${accent ? "text-white" : "text-zinc-700"}`} />
        </div>
      </div>
    </div>
  );
}

export default function ProjectDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/projects/${id}/detail`).then((r) => setData(r.data)).catch(() => nav("/projects"));
  }, [id, nav]);

  if (!data) {
    return (
      <div>
        <Skeleton className="h-8 w-96 mb-4" />
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      </div>
    );
  }

  const { project: p, documents, work_orders, invoices, machinery, totals } = data;

  return (
    <div data-testid="project-detail-page">
      <Link
        to="/projects"
        data-testid="project-detail-back"
        className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-orange-600 mb-4"
      >
        <ArrowLeft className="h-3 w-3" /> Back to Projects
      </Link>

      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="font-mono text-[10px] px-1.5 py-0.5 border border-zinc-300 rounded-sm">
              {p.project_type}
            </span>
            <span className="flex items-center gap-1.5 text-xs text-zinc-600">
              <span className={`h-1.5 w-1.5 rounded-full ${STATUS_COLORS[p.status] || "bg-zinc-400"}`} />
              {p.status}
            </span>
          </div>
          <h1 className="font-heading font-black text-4xl tracking-tight">{p.name}</h1>
          <div className="flex items-center gap-4 text-xs text-zinc-500 mt-2">
            <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.location || "—"}</span>
            {p.start_date && (
              <span className="flex items-center gap-1 font-mono">
                <Calendar className="h-3 w-3" /> {p.start_date} → {p.end_date || "?"}
              </span>
            )}
          </div>
          {p.description && <div className="text-sm text-zinc-600 mt-3 max-w-2xl">{p.description}</div>}
        </div>
        <div className="text-right">
          <div className="overline">Contract Value</div>
          <div className="font-heading font-black text-3xl text-orange-600">{fmtINR(p.contract_value)}</div>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Kpi label="Documents" value={totals.documents} icon={FileText} />
        <Kpi label="Work Orders" value={totals.work_orders} icon={ClipboardList} />
        <Kpi label="Invoices" value={totals.invoices} icon={Receipt} />
        <Kpi label="Machinery" value={totals.machinery} icon={Truck} />
        <Kpi label="Receivable" value={fmtINR(totals.receivable)} icon={TrendingUp} accent="bg-green-600" />
        <Kpi label="Payable" value={fmtINR(totals.payable)} icon={TrendingDown} accent="bg-red-600" />
        <Kpi label="Paid" value={fmtINR(totals.paid)} icon={IndianRupee} />
        <Kpi label="Pending" value={fmtINR(totals.pending)} icon={IndianRupee} accent="bg-yellow-500" />
      </div>

      <Tabs defaultValue="documents" className="w-full">
        <TabsList data-testid="project-tabs" className="rounded-sm bg-zinc-100 border border-zinc-200 h-10">
          <TabsTrigger value="documents" data-testid="tab-documents" className="rounded-sm data-[state=active]:bg-white">
            Documents ({totals.documents})
          </TabsTrigger>
          <TabsTrigger value="work_orders" data-testid="tab-work-orders" className="rounded-sm data-[state=active]:bg-white">
            Work Orders ({totals.work_orders})
          </TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices" className="rounded-sm data-[state=active]:bg-white">
            Invoices ({totals.invoices})
          </TabsTrigger>
          <TabsTrigger value="machinery" data-testid="tab-machinery" className="rounded-sm data-[state=active]:bg-white">
            Machinery ({totals.machinery})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="documents" className="mt-4">
          <SimpleTable
            columns={[
              { key: "original_name", label: "Document" },
              { key: "category", label: "Category" },
              { key: "expiry_date", label: "Expiry" },
              { key: "text_length", label: "Indexed" },
              { key: "created_at", label: "Uploaded" },
            ]}
            rows={documents}
            empty="No documents linked to this project."
          />
        </TabsContent>
        <TabsContent value="work_orders" className="mt-4">
          <SimpleTable
            columns={[
              { key: "wo_number", label: "WO #" },
              { key: "description", label: "Description" },
              { key: "amount", label: "Amount", type: "money" },
              { key: "status", label: "Status" },
              { key: "issue_date", label: "Date" },
            ]}
            rows={work_orders}
            empty="No work orders on this project."
          />
        </TabsContent>
        <TabsContent value="invoices" className="mt-4">
          <SimpleTable
            columns={[
              { key: "invoice_number", label: "Invoice #" },
              { key: "invoice_type", label: "Type" },
              { key: "amount", label: "Amount", type: "money" },
              { key: "status", label: "Status" },
              { key: "due_date", label: "Due" },
            ]}
            rows={invoices}
            empty="No invoices for this project."
          />
        </TabsContent>
        <TabsContent value="machinery" className="mt-4">
          <SimpleTable
            columns={[
              { key: "name", label: "Machine" },
              { key: "machine_type", label: "Type" },
              { key: "registration_number", label: "Reg. No." },
              { key: "status", label: "Status" },
            ]}
            rows={machinery}
            empty="No machinery assigned to this project."
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SimpleTable({ columns, rows, empty }) {
  if (!rows?.length) {
    return (
      <div className="border border-dashed border-zinc-300 bg-white rounded-sm p-10 text-center text-zinc-400 font-mono text-xs">
        {empty}
      </div>
    );
  }
  const render = (row, col) => {
    const v = row[col.key];
    if (v === undefined || v === null || v === "") return "—";
    if (col.type === "money") return `₹${Number(v).toLocaleString("en-IN")}`;
    if (col.key === "created_at" || col.key === "expiry_date" || col.key === "due_date" || col.key === "issue_date") {
      return String(v).slice(0, 10);
    }
    if (col.key === "text_length") {
      return v > 0 ? <span className="text-xs text-green-700 font-mono">{v.toLocaleString()} chars</span> : <span className="text-xs text-zinc-400">no text</span>;
    }
    if (col.key === "category" || col.key === "status" || col.key === "invoice_type" || col.key === "machine_type") {
      return <span className="font-mono text-[10px] px-2 py-0.5 border border-zinc-300 rounded-sm">{v}</span>;
    }
    return String(v);
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50">
          <tr className="text-left">
            {columns.map((c) => <th key={c.key} className="px-4 py-2.5 overline">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-zinc-100 hover:bg-zinc-50">
              {columns.map((c) => <td key={c.key} className="px-4 py-3">{render(r, c)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
